import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Partners.css';

type PartnerType = 'labour' | 'material' | 'architect' | 'builder';
type PartnerStatus = 'pending' | 'active' | 'rejected' | 'archived';

interface Partner {
  id: number;
  partnerType: PartnerType;
  status: PartnerStatus;
  name: string;
  contactPerson: string | null;
  mobile: string;
  email: string | null;
  address: string | null;
  category: string | null;
  aadharNumber: string | null;
  gstNumber: string | null;
  bankAccount: string | null;
  ifsc: string | null;
  upiId: string | null;
  about: string | null;
  createdAt: string;
}

interface PartnerFull extends Partner {
  aadharPhoto: string | null;
  selfiePhoto: string | null;
  shopRCPhoto: string | null;
  companyRCPhoto: string | null;
}

const TYPE_LABELS: Record<PartnerType, string> = {
  labour: 'Labour',
  material: 'Material',
  architect: 'Architect',
  builder: 'Builder',
};

const TYPE_ICONS: Record<PartnerType, string> = {
  labour: '👷',
  material: '🏗️',
  architect: '📐',
  builder: '🏢',
};

type TypeFilter = 'all' | PartnerType;
type StatusFilter = 'all' | PartnerStatus;

const Partners: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [openDetail, setOpenDetail] = useState<PartnerFull | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const res = await api.get('/partners');
      setPartners(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const openPartner = async (id: number) => {
    setOpenId(id);
    setOpenDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.get(`/partners/${id}`);
      setOpenDetail(res.data);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to load details');
      setOpenId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closePartner = () => {
    setOpenId(null);
    setOpenDetail(null);
  };

  const updateStatus = async (id: number, status: PartnerStatus) => {
    try {
      await api.patch(`/partners/${id}/status`, { status });
      setPartners(ps => ps.map(p => (p.id === id ? { ...p, status } : p)));
      if (openDetail?.id === id) setOpenDetail({ ...openDetail, status });
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update status');
    }
  };

  const deletePartner = async (id: number, name: string) => {
    if (!window.confirm(`Delete partner "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/partners/${id}`);
      setPartners(ps => ps.filter(p => p.id !== id));
      if (openId === id) closePartner();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  };

  const needle = search.trim().toLowerCase();
  const filtered = partners.filter(p => {
    if (typeFilter !== 'all' && p.partnerType !== typeFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (needle) {
      const hay = [p.name, p.contactPerson, p.mobile, p.email, p.category, p.address]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  // Group by date
  const groups: { dateKey: string; label: string; items: Partner[] }[] = [];
  const groupMap: Record<string, Partner[]> = {};
  filtered.forEach(p => {
    const key = new Date(p.createdAt).toISOString().slice(0, 10);
    if (!groupMap[key]) groupMap[key] = [];
    groupMap[key].push(p);
  });
  Object.keys(groupMap).sort((a, b) => (a < b ? 1 : -1)).forEach(key => {
    const label = new Date(key + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    groups.push({ dateKey: key, label, items: groupMap[key] });
  });

  const counts = {
    total: partners.length,
    pending: partners.filter(p => p.status === 'pending').length,
    active: partners.filter(p => p.status === 'active').length,
    rejected: partners.filter(p => p.status === 'rejected').length,
    archived: partners.filter(p => p.status === 'archived').length,
  };

  return (
    <div className="pp-page">
      <header className="pp-header">
        <div className="pp-header-left">
          <h1>arcivi</h1>
          <span className="pp-badge">Partner Directory</span>
        </div>
        <div className="pp-header-right">
          <button className="pp-btn-ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
          <button className="pp-btn-ghost" onClick={logout}>Logout</button>
        </div>
      </header>

      <div className="pp-content">
        <div className="pp-title">
          <h2>Partner Applications</h2>
          {counts.pending > 0 && <span className="pp-new-badge">{counts.pending} pending review</span>}
        </div>

        <div className="pp-controls">
          <input
            className="pp-search"
            placeholder="Search by name, phone, email, category, location…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="pp-filter-row">
          <div className="pp-filter-label">Type</div>
          <div className="pp-tabs">
            <button className={typeFilter === 'all' ? 'pp-tab active' : 'pp-tab'} onClick={() => setTypeFilter('all')}>All</button>
            {(['labour', 'material', 'architect', 'builder'] as PartnerType[]).map(t => (
              <button
                key={t}
                className={typeFilter === t ? 'pp-tab active' : 'pp-tab'}
                onClick={() => setTypeFilter(t)}
              >
                {TYPE_ICONS[t]} {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        <div className="pp-filter-row">
          <div className="pp-filter-label">Status</div>
          <div className="pp-tabs">
            <button className={statusFilter === 'all' ? 'pp-tab active' : 'pp-tab'} onClick={() => setStatusFilter('all')}>All ({counts.total})</button>
            <button className={statusFilter === 'pending' ? 'pp-tab active' : 'pp-tab'} onClick={() => setStatusFilter('pending')}>Pending ({counts.pending})</button>
            <button className={statusFilter === 'active' ? 'pp-tab active' : 'pp-tab'} onClick={() => setStatusFilter('active')}>Active ({counts.active})</button>
            <button className={statusFilter === 'rejected' ? 'pp-tab active' : 'pp-tab'} onClick={() => setStatusFilter('rejected')}>Rejected ({counts.rejected})</button>
            <button className={statusFilter === 'archived' ? 'pp-tab active' : 'pp-tab'} onClick={() => setStatusFilter('archived')}>Archived ({counts.archived})</button>
          </div>
        </div>

        {loading && <div className="pp-loading">Loading partners…</div>}
        {error && <div className="pp-error">{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="pp-empty">No partners match the current filters.</div>
        )}

        {groups.map(group => (
          <div key={group.dateKey} className="pp-group">
            <h3 className="pp-group-label">{group.label}</h3>
            <div className="pp-list">
              {group.items.map(p => (
                <div key={p.id} className={`pp-card pp-card--${p.status}`}>
                  <div className="pp-card-top">
                    <div className="pp-avatar">{TYPE_ICONS[p.partnerType]}</div>
                    <div className="pp-who">
                      <h4>{p.name}</h4>
                      <div className="pp-sub">
                        <span className="pp-type">{TYPE_LABELS[p.partnerType]}</span>
                        {p.category && <span>• {p.category}</span>}
                      </div>
                      <div className="pp-meta">
                        {p.mobile && <span>📞 <a href={`tel:${p.mobile}`}>{p.mobile}</a></span>}
                        {p.email && <span>✉️ <a href={`mailto:${p.email}`}>{p.email}</a></span>}
                      </div>
                    </div>
                    <div className="pp-status">
                      <span className={`pp-chip pp-chip--${p.status}`}>{p.status}</span>
                    </div>
                  </div>

                  <div className="pp-actions">
                    <button className="pp-btn pp-btn--view" onClick={() => openPartner(p.id)}>
                      View Details
                    </button>
                    {p.status !== 'active' && (
                      <button className="pp-btn pp-btn--approve" onClick={() => updateStatus(p.id, 'active')}>
                        Approve
                      </button>
                    )}
                    {p.status !== 'rejected' && (
                      <button className="pp-btn pp-btn--reject" onClick={() => updateStatus(p.id, 'rejected')}>
                        Reject
                      </button>
                    )}
                    {p.status !== 'archived' && (
                      <button className="pp-btn pp-btn--archive" onClick={() => updateStatus(p.id, 'archived')}>
                        Archive
                      </button>
                    )}
                    <button className="pp-btn pp-btn--delete" onClick={() => deletePartner(p.id, p.name)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {openId !== null && (
        <div className="pp-modal" onClick={closePartner}>
          <div className="pp-modal-card" onClick={e => e.stopPropagation()}>
            {detailLoading && <div className="pp-loading">Loading details…</div>}
            {openDetail && (
              <>
                <div className="pp-modal-header">
                  <div>
                    <h3>{openDetail.name}</h3>
                    <div className="pp-sub">
                      <span className="pp-type">{TYPE_LABELS[openDetail.partnerType]}</span>
                      {openDetail.category && <span>• {openDetail.category}</span>}
                      <span className={`pp-chip pp-chip--${openDetail.status}`}>{openDetail.status}</span>
                    </div>
                  </div>
                  <button className="pp-modal-close" onClick={closePartner} aria-label="Close">✕</button>
                </div>

                <div className="pp-modal-body">
                  <DetailRow label="Contact Person" value={openDetail.contactPerson} />
                  <DetailRow label="Mobile" value={openDetail.mobile} />
                  <DetailRow label="Email" value={openDetail.email} />
                  <DetailRow label="Address" value={openDetail.address} />

                  <h4 className="pp-modal-group">Identity & Documents</h4>
                  <DetailRow label="Aadhar Number" value={openDetail.aadharNumber} />
                  <DetailRow label="GST Number" value={openDetail.gstNumber} />

                  <div className="pp-docs">
                    {openDetail.aadharPhoto && <DocPreview label="Aadhar" src={openDetail.aadharPhoto} />}
                    {openDetail.selfiePhoto && <DocPreview label="Selfie" src={openDetail.selfiePhoto} />}
                    {openDetail.shopRCPhoto && <DocPreview label="Shop RC" src={openDetail.shopRCPhoto} />}
                    {openDetail.companyRCPhoto && <DocPreview label="Company RC" src={openDetail.companyRCPhoto} />}
                  </div>

                  <h4 className="pp-modal-group">Payment Details</h4>
                  <DetailRow label="Bank Account" value={openDetail.bankAccount} />
                  <DetailRow label="IFSC" value={openDetail.ifsc} />
                  <DetailRow label="UPI ID" value={openDetail.upiId} />

                  {openDetail.about && (
                    <>
                      <h4 className="pp-modal-group">About</h4>
                      <p className="pp-about">{openDetail.about}</p>
                    </>
                  )}
                </div>

                <div className="pp-modal-footer">
                  {openDetail.status !== 'active' && (
                    <button className="pp-btn pp-btn--approve" onClick={() => updateStatus(openDetail.id, 'active')}>
                      Approve
                    </button>
                  )}
                  {openDetail.status !== 'rejected' && (
                    <button className="pp-btn pp-btn--reject" onClick={() => updateStatus(openDetail.id, 'rejected')}>
                      Reject
                    </button>
                  )}
                  <button className="pp-btn pp-btn--delete" onClick={() => deletePartner(openDetail.id, openDetail.name)}>
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="pp-row">
      <span className="pp-row-label">{label}</span>
      <span className="pp-row-value">{value}</span>
    </div>
  );
};

const DocPreview: React.FC<{ label: string; src: string }> = ({ label, src }) => (
  <a className="pp-doc" href={src} target="_blank" rel="noopener noreferrer">
    <img src={src} alt={label} />
    <span>{label}</span>
  </a>
);

export default Partners;
